import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Invitation } from '@app/+state/invitation/domain';
import { ReservationInfo, Trial, TrialBundle, TrialReservation } from '@app/+state/trials/trials.model';
import { UserRoleHierarchy } from '@app/+state/user-access';
import { TENANT_MGMT_ROUTES, TenantListPage, TenantRow, UserMembership } from '@app/+state/user-data/user.model';
import { ENV_VARS } from '@app/models/config.model';
import { checkIfTrialEntitlement, getEnvOrDefault, replaceTenantLogoUrlHost } from '@app/v2/shared/utils';
import { ApiError } from '@core/api-error';
import { AppErrorCode, getErrorMessage } from '@core/common-constants';
import { environment } from '@environments/environment';
import { EntitylementTypes } from '@lemans/shared';
import {
  AllRecordsRequest,
  ApproveUserRecord,
  Case,
  ControlPageFacade,
  DefaultLanguage,
  EffectiveRole,
  Entitlement,
  EntitlementCounts,
  FavoriteApps,
  ICatalogInfo,
  IEulaConfig,
  ITenantDetail,
  IUpdatedPreferences,
  LedgerEntry,
  LedgerRequest,
  LogoUrl,
  NoContentApiResponse,
  PageRequest,
  PageResponse,
  PaginatedTableResponse,
  ResourceType,
  SignalRFacade,
  SnackBarData,
  SortFilterContext,
  TenantHierarchyUsersRequest,
  TenantResourceHierarchy,
  TenantService,
  TenantUser,
  TenantUserAccess,
  TenantUtilityTokens,
  TenantUtilityTokensResponse,
  UiStateFacade,
  UnallocatedEntitlementCounts,
  UserDataFacade,
  UserFeedback,
  UserPreferences,
  UserRole,
  Version,
} from '@ra-state';
import { AppId, tryParseAppId } from '@rockwell-automation-inc/common-utils';
import { ConfigService, CoreConfiguration } from '@rockwell-automation-inc/service';
import * as R from 'remeda';
import { combineLatest, EMPTY, expand, Observable, of, reduce, throwError } from 'rxjs';
import { catchError, map, withLatestFrom } from 'rxjs/operators';
import { CommandRequest } from './command-request.service';
import { LoggerService } from './logger.service';

class CatalogInfoImpl implements ICatalogInfo {
  constructor(data: any) {
    this.catalogCode = data.CatalogCode;
    this.name = data.name;
    this.isCredit = data.IsCredit;
    if (data.serviceKind) {
      // REVISIT: fix tryParseAppId so it does not fail on undefined
      this.serviceKind = tryParseAppId(data.serviceKind);
    }
    this.isMockAllowed = data.IsMockAllowed;
  }
  catalogCode: string;
  name: string;
  description: string;
  serviceKind?: AppId;
  isCredit: boolean;
  isMockAllowed: boolean;
  get appName(): string {
    return environment.appConfiguration.appName(this.serviceKind || '');
  }
}

type ApiVersion = {
  application: string;
  version: string;
  processName: string;
  productVersion: string;
};

@Injectable()
export class DataService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject<ConfigService<CoreConfiguration>>(ConfigService);
  private readonly logger = inject(LoggerService).withContext('DataService');
  private readonly controlPageFacade = inject(ControlPageFacade);
  private readonly uiStateFacade = inject(UiStateFacade);
  private readonly router = inject(Router);
  private readonly signalRFacade = inject(SignalRFacade);
  private readonly userDataFacade = inject(UserDataFacade);

  private baseUrl: string;
  private userBaseUrl: string;
  private tenantBaseUrl: string;
  private suggestedTenantsUrl: string;
  private eulaConfigBaseUrl: string;
  private notificationBaseUrl: string;
  private suggestedTenantByCodeUrl: string;
  private invitationsUrl: string;
  private approveUsers: string;
  private trialBaseUrl: string;
  private trialBundlesUrl: string;
  user: UserPreferences;
  catalogUrl: string;
  cspBaseUrl: string;

  constructor() {}

  // Call this on control page effects when auth0 is resolved
  initializeEndpoints(): void {
    this.baseUrl = this.configService.config.csApiBaseUrl;
    this.userBaseUrl = `${this.baseUrl}${ApiEndPoints.Users}`;
    this.tenantBaseUrl = `${this.baseUrl}${ApiEndPoints.Tenant}`;
    this.eulaConfigBaseUrl = `${this.baseUrl}${ApiEndPoints.Eula}`;
    this.catalogUrl = `${this.baseUrl}${ApiEndPoints.Catalog}`;
    this.suggestedTenantsUrl = `${this.baseUrl}${ApiEndPoints.SuggestedTenants}`;
    this.suggestedTenantByCodeUrl = `${this.baseUrl}${ApiEndPoints.SuggestedTenantByCode}`;
    this.notificationBaseUrl = this.configService.config.notificationsBaseUrl;
    this.invitationsUrl = `${this.baseUrl}${ApiEndPoints.Invitations}`;
    this.approveUsers = `${this.baseUrl}${ApiEndPoints.AccessRequest}`;
    this.trialBaseUrl = `${this.baseUrl}${ApiEndPoints.Trial}`;
    this.trialBundlesUrl = `${this.baseUrl}${ApiEndPoints.TrialBundles}`;
    this.cspBaseUrl = `${this.baseUrl}${ApiEndPoints.CspUrls}`;
  }

  getUser$(userId: string): Observable<UserPreferences> {
    const url = `${this.userBaseUrl}${userId}/preferences`;
    return this.http.get<UserPreferences>(url).pipe(
      map((user) => {
        const favoriteApps = user.preferences?.favoriteApps
          ? (JSON.parse(user.preferences?.favoriteApps as any) as FavoriteApps)
          : {};
        if (user.preferences) {
          user.preferences.favoriteApps = favoriteApps;
          user.preferences.language = user.preferences?.language ?? DefaultLanguage;
        }

        return user;
      }),
      catchError((error: unknown) => this.ignoreNotFoundError$(error)),
    );
  }

  setAllowDuringMaintenanceHeader(headers?: HttpHeaders): HttpHeaders {
    const hdrs = headers || new HttpHeaders();
    return hdrs.set('allow_during_maintenance', 'true');
  }

  updateUserPreferences$(userId: string, updatedPreferences: IUpdatedPreferences): Observable<NoContentApiResponse> {
    const url = `${this.userBaseUrl}${userId}/preferences`;
    return this.http
      .put<NoContentApiResponse>(url, updatedPreferences, {
        headers: this.setAllowDuringMaintenanceHeader(),
        observe: 'response',
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getEulaConfig$(): Observable<IEulaConfig> {
    const url = this.eulaConfigBaseUrl;
    return this.http.get<IEulaConfig>(url).pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getCatalogInfo$(): Observable<ICatalogInfo[]> {
    return this.http.get<any[]>(this.catalogUrl).pipe(
      map((catalogResp) =>
        catalogResp.map((catalog) => {
          return new CatalogInfoImpl(catalog);
        }),
      ),
      catchError((error: unknown) => this.handleError$(error)),
    );
  }

  getSuggestedTenants$(
    pageRequest: PageRequest,
    sortFilterContext: SortFilterContext,
  ): Observable<PageResponse<ITenantDetail>> {
    const url = `${this.suggestedTenantsUrl}?perPage=${pageRequest.perPage}&pageNum=${pageRequest.pageNum}`;
    const sortFilterModel = sortFilterContext ?? {};
    return this.http
      .get<PageResponse<ITenantDetail>>(url, {
        params: {
          sortFilterOpts: JSON.stringify(sortFilterModel),
        },
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getSuggestedTenantByCode$(inviteCode: string): Observable<ITenantDetail[]> {
    const url = `${this.suggestedTenantByCodeUrl}${inviteCode}`;
    return this.http.get<ITenantDetail[]>(url).pipe(catchError((error: unknown) => this.ignoreNotFoundError$(error)));
  }

  setTenantIdHeader(headers: HttpHeaders, tenantId: string): HttpHeaders {
    return headers.set('tenantid', tenantId);
  }

  getTenantEffectiveRoles$(tenantId: string, userId: string): Observable<EffectiveRole[]> {
    const url = `${this.userBaseUrl}${userId}/tenanteffectiveroles`;
    return this.http
      .get<EffectiveRole[]>(url, { headers: this.setTenantIdHeader(new HttpHeaders(), tenantId) })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getTenantResourceRoles$(
    pageRequest: PageRequest,
    sortFilterContext: SortFilterContext,
  ): Observable<PageResponse<UserRole>> {
    const url = `${this.tenantBaseUrl}resourceroles?perPage=${pageRequest.perPage}&pageNum=${pageRequest.pageNum}`;
    const sortFilterModel = sortFilterContext ?? {};

    return this.http
      .get<PageResponse<UserRole>>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), String(pageRequest.tenantId)),
        params: {
          sortFilterOpts: JSON.stringify(sortFilterModel),
        },
      })
      .pipe(
        withLatestFrom(this.userDataFacade.currentTenant$),

        map(([resourceroles, currentTenant]) => {
          if (currentTenant?.parent) {
            return {
              ...resourceroles,
              records: resourceroles.records.map((role) => {
                if (role.resourceType === ResourceType.Tenant) {
                  return {
                    ...role,
                    isSubOrg: Boolean(currentTenant.parent),
                  };
                } else {
                  return role;
                }
              }),
            };
          }
          return resourceroles;
        }),
        catchError((error: unknown) => this.handleError$(error)),
      );
  }

  getUserResources$(tenantId: string): Observable<any> {
    const url = `${this.tenantBaseUrl}resources`;
    const headers = new HttpHeaders().set('tenantId', tenantId);
    return this.http.get<any>(url, { headers }).pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getAllTenantResourceRoles$(tenantId: string, userId?: string): Observable<UserRole[]> {
    const url = `${this.tenantBaseUrl}resourceroles`;
    let httpParams = new HttpParams();
    if (userId) {
      httpParams = httpParams.append('user', userId);
    }
    const headers = this.setTenantIdHeader(new HttpHeaders(), tenantId);
    return this.getAllRecords$<UserRole>({
      url: url,
      httpParams: httpParams,
      headers: headers,
    });
  }

  getTenantHierarchyUsers$(
    tenantHierarchyUsersRequest: TenantHierarchyUsersRequest,
  ): Observable<PageResponse<TenantUser>> {
    const url = `${this.baseUrl}${ApiEndPoints.Tenant}hierarchy/users?perPage=${tenantHierarchyUsersRequest.perPage}&pageNum=${tenantHierarchyUsersRequest.pageNum}&includeTree=${tenantHierarchyUsersRequest.includeTree}&includeSiblings=${tenantHierarchyUsersRequest.includeSiblings}`;

    return this.http
      .get<PageResponse<any>>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), tenantHierarchyUsersRequest.tenantId),
        params: {
          sortFilterOpts: JSON.stringify(tenantHierarchyUsersRequest.sortFilterContext),
        },
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getTenantHierarchyUsersAccess$(
    tenantHierarchyUsersAccessRequest: Required<PageRequest>,
  ): Observable<PageResponse<TenantUserAccess>> {
    const url = `${this.baseUrl}${ApiEndPoints.Tenant}hierarchy/usersAccess?perPage=${tenantHierarchyUsersAccessRequest.perPage}&pageNum=${tenantHierarchyUsersAccessRequest.pageNum}`;

    return this.http
      .get<PageResponse<TenantUserAccess>>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), tenantHierarchyUsersAccessRequest.tenantId),
        params: {
          sortFilterOpts: JSON.stringify(tenantHierarchyUsersAccessRequest.sortFilterContext),
        },
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getAllRecords$<T>(request: AllRecordsRequest): Observable<T[]> {
    const startPageNum = 1;
    let fetchedCount = 0;
    const perPage = request.perPage ?? 10;
    let httpParams = request.httpParams
      ? request.httpParams.append('perPage', perPage).append('pageNum', startPageNum)
      : new HttpParams().set('perPage', perPage).set('pageNum', startPageNum);
    const headers = request.headers;

    return this.http.get<PageResponse<T>>(request.url, { headers: request.headers, params: httpParams }).pipe(
      expand((response) => {
        fetchedCount = fetchedCount + response.records.length;
        httpParams = httpParams.set('pageNum', response.currentPage + 1);
        return fetchedCount === response.total
          ? EMPTY
          : this.http.get<PageResponse<T>>(request.url, {
              headers: headers,
              params: httpParams,
            });
      }),
      map((res) => res.records),
      reduce((acc: T[], current: T[]) => acc.concat(current), []),
    );
  }

  shareFeedback$(userFeedback: UserFeedback): Observable<NoContentApiResponse> {
    const url = `${this.baseUrl}${ApiEndPoints.Feedback}`;
    return this.http
      .post<NoContentApiResponse>(url, userFeedback)
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getEntitlements$(
    userId: string,
    pageRequest: PageRequest,
    sortFilterContext: SortFilterContext,
  ): Observable<PageResponse<Entitlement>> {
    const url = `${this.userBaseUrl}${userId}/entitlements?perPage=${pageRequest.perPage}&pageNum=${pageRequest.pageNum}`;
    const sortFilterModel = sortFilterContext ?? {};
    return this.http
      .get<PageResponse<Entitlement>>(url, {
        params: {
          sortFilterOpts: JSON.stringify(sortFilterModel),
        },
      })
      .pipe(
        map((data) => {
          data.records.forEach((entitlement) => {
            // REVISIT: remove this piece of code when the backend provides the correct combineType
            if (R.isEmpty(entitlement.attributes?.combineType) && entitlement.serviceKind === '') {
              entitlement.attributes = {
                ...entitlement.attributes,
                combineType: EntitylementTypes.utilityToken,
              };
            }
            entitlement.isTrialEntitlement = checkIfTrialEntitlement(entitlement);
          });

          return data;
        }),
        catchError((error: unknown) => this.handleError$(error)),
      );
  }

  getRedeemedTrials$(
    userId: string,
    pageRequest: PageRequest,
    sortFilterContext: SortFilterContext,
  ): Observable<PageResponse<Trial>> {
    const url = `${this.userBaseUrl}${userId}/trials?perPage=${pageRequest.perPage}&pageNum=${pageRequest.pageNum}`;
    const sortFilterModel = sortFilterContext ?? {};
    return this.http
      .get<PageResponse<Trial>>(url, {
        params: {
          sortFilterOpts: JSON.stringify(sortFilterModel),
        },
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  allocateTrial$(userId: string, trialId: string, tenantId: string): Observable<NoContentApiResponse> {
    const url = `${this.userBaseUrl}${userId}/trials/${trialId}/tenantallocate`;
    return this.http
      .put<NoContentApiResponse>(url, null, {
        headers: this.setTenantIdHeader(new HttpHeaders(), String(tenantId)),
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getAllocatedTrials$(pageRequest: PageRequest, sortFilterContext: SortFilterContext): Observable<PageResponse<Trial>> {
    const url = `${this.tenantBaseUrl}trials?perPage=${pageRequest.perPage}&pageNum=${pageRequest.pageNum}`;
    const sortFilterModel = sortFilterContext ?? {};
    return this.http
      .get<PageResponse<Trial>>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), String(pageRequest.tenantId)),
        params: {
          sortFilterOpts: JSON.stringify(sortFilterModel),
        },
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getInvitations$(
    pageRequest: PageRequest,
    sortFilterContext: SortFilterContext,
  ): Observable<PageResponse<Invitation>> {
    const url = `${this.invitationsUrl}?perPage=${pageRequest.perPage}&pageNum=${pageRequest.pageNum}`;
    const sortFilterModel = sortFilterContext ?? {};
    return this.http
      .get<PageResponse<Invitation>>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), String(pageRequest.tenantId)),
        params: {
          sortFilterOpts: JSON.stringify(sortFilterModel),
        },
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getUploadLogoUrl$(tenantId: string): Observable<LogoUrl> {
    const url = `${this.baseUrl}/api/Tenant/logoUploadUri`;
    return this.http
      .get<LogoUrl>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), tenantId),
      })
      .pipe(catchError(this.handleError$.bind(this)));
  }

  uploadOrgLogo$(url: string, blobImage: Blob): Observable<HttpResponse<void>> {
    let headers = new HttpHeaders();
    headers = headers.set('x-ms-blob-type', 'BlockBlob');
    const supportedImageTypes = ['image/png', 'image/jpeg'];
    const contentType = supportedImageTypes.includes(blobImage.type) ? blobImage.type : 'image/png';
    headers = headers.set('Content-Type', contentType);

    return this.http
      .put<void>(url, blobImage, { headers, observe: 'response' })
      .pipe(catchError((error: unknown) => this.handleTenantLogoUploadError$(error)));
  }

  getVersions$(): Observable<Version> {
    const UNKNOWN_VERSION = 'Unknown/Local';
    const UNKNOWN_VERSION$ = (err: unknown): Observable<string> => {
      this.logger.warn('error', err);
      return of(UNKNOWN_VERSION);
    };
    const apiVersionUrl = `${this.baseUrl}${ApiEndPoints.Version}`;
    const notificationServiceVersion = `${this.notificationBaseUrl}/api/version`;
    return combineLatest([
      this.http.get<ApiVersion>(apiVersionUrl).pipe(
        map((v) => v.productVersion),
        catchError(UNKNOWN_VERSION$),
      ),
      this.http.get<ApiVersion>(notificationServiceVersion).pipe(
        map((v) => v.productVersion),
        catchError(UNKNOWN_VERSION$),
      ),
      of(getEnvOrDefault(ENV_VARS.VERSION, UNKNOWN_VERSION)),
    ]).pipe(
      map(([apiVersion, notificationsVersion, portalVersion]) => {
        return {
          apiVersion: apiVersion,
          portalVersion: portalVersion,
          notificationsVersion: notificationsVersion,
        };
      }),
    );
  }

  getApproveUserList$(pageRequest: PageRequest): Observable<PageResponse<ApproveUserRecord>> {
    const url = `${this.approveUsers}?perPage=${pageRequest.perPage}&pageNum=${pageRequest.pageNum}`;
    return this.http
      .get<PageResponse<ApproveUserRecord>>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), String(pageRequest.tenantId)),
        params: {
          sortFilterOpts: JSON.stringify(pageRequest.sortFilterContext ?? {}),
        },
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getTenantServices$(userId: string, tenantId: string): Observable<TenantService[]> {
    const url = `${this.userBaseUrl}${userId}/tenantservices`;
    const headers = new HttpHeaders().set('tenantId', tenantId);
    return this.http
      .get<TenantService[]>(url, { headers })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getTenantUtilityTokens$(tenantId: string): Observable<TenantUtilityTokens> {
    const url = `${this.tenantBaseUrl}utilitytokens`;
    return this.http
      .get<TenantUtilityTokensResponse>(url, { headers: this.setTenantIdHeader(new HttpHeaders(), tenantId) })
      .pipe(
        map((utilityToken) => {
          const tokenEnts = utilityToken.tokenEntitlements.map((ent) => ({
            ...ent,
            id: ent.entitlementId,
            isTrialEntitlement: checkIfTrialEntitlement(ent),
          }));
          const disabledTokenEnts = utilityToken.disabledTokenEntitlements.map((ent) => ({
            ...ent,
            id: ent.entitlementId,
            isTrialEntitlement: checkIfTrialEntitlement(ent),
          }));
          return {
            id: utilityToken.id,
            tokenBalance: utilityToken.tokenBalance,
            tokenExpiration: utilityToken.tokenExpiration,
            tokenEntitlements: tokenEnts,
            disabledTokenEntitlements: disabledTokenEnts,
          };
        }),
        catchError((error: unknown) => this.handleError$(error)),
      );
  }

  getTenantLedger$(ledgerRequest: LedgerRequest): Observable<PaginatedTableResponse<LedgerEntry>> {
    let params = new HttpParams()
      .set('from', ledgerRequest.from.toISOString())
      .set('duration', ledgerRequest.duration)
      .set('export', ledgerRequest.forExport);

    if (ledgerRequest.perPage) {
      params = params.set('perPage', ledgerRequest.perPage);
    }
    if (ledgerRequest.token) {
      params = params.set('token', ledgerRequest.token);
    }

    const url = `${this.tenantBaseUrl}utilitytokens/ledger`;
    return this.http
      .get<PaginatedTableResponse<LedgerEntry>>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), ledgerRequest.tenantId),
        params: params,
      })
      .pipe(
        map((ledgerEntries) => {
          ledgerEntries.records.forEach((record) => {
            record.Item.timestamp = record.Item.ts;
            if (record.Case === Case.Debit) {
              const debitRec = record.Item;
              debitRec.serviceKind = tryParseAppId(debitRec.serviceKind) ?? debitRec.serviceKind;
            }
          });
          return ledgerEntries;
        }),
      );
  }

  getTrialBundles$(): Observable<TrialBundle[]> {
    const url = `${this.trialBundlesUrl}`;
    return this.http.get<TrialBundle[]>(url).pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getCspUrls$(): Observable<{
    [key: string]: string;
  }> {
    const url = `${this.cspBaseUrl}`;
    return this.http
      .get<{
        [key: string]: string;
      }>(url)
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getTrialReservationInfo$(request: ReservationInfo): Observable<TrialReservation> {
    const url = `${this.trialBaseUrl}/${request.campaignId}/reservations/${request.trialReservationId}`;
    return this.http.get<TrialReservation>(url).pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getUnAllocatedEntitlements$(userId: string): Observable<UnallocatedEntitlementCounts> {
    const url = `${this.userBaseUrl}${userId}/entitlements/unallocated`;
    return this.http.get<EntitlementCounts[]>(url).pipe(
      map((entitlementCounts) => {
        const entCounts = entitlementCounts.map((entitlementCount) => {
          return {
            ...entitlementCount,
            totalEntitlements: entitlementCount.unallocatedCredits + entitlementCount.unallocatedServices,
          };
        });

        const totalCreditsEntitlementCount =
          entCounts.find((entitlement) => entitlement.supportsCredits)?.unallocatedCredits || 0;
        const totalEntitlementsCount =
          entCounts.reduce((sum, entCount) => sum + entCount.unallocatedServices, 0) + totalCreditsEntitlementCount;

        return {
          entitlementCounts: entCounts,
          totalEntitlementCount: totalEntitlementsCount,
          totalCreditsEntitlementCount: totalCreditsEntitlementCount,
        };
      }),
      catchError((error: unknown) => this.handleError$(error)),
    );
  }

  getTenantList$(
    apiEndPointType: 'tenants' | 'sub-tenants',
    pageRequest: PageRequest,
    sortFilterContext: SortFilterContext,
    all?: boolean,
    query?: string,
  ): Observable<TenantListPage<TenantRow>> {
    const params = new HttpParams()
      .set('perPage', pageRequest.perPage)
      .set('pageNum', pageRequest.pageNum)
      .set('sortFilterOpts', JSON.stringify(sortFilterContext ?? {}));

    const queryParams = all ? params.set('all', all.toString()) : params;
    const finalParams = query ? queryParams.set('query', query) : queryParams;

    if (apiEndPointType === 'tenants') {
      return this.http
        .get<TenantListPage<TenantRow>>(`${this.userBaseUrl}tenants`, {
          params: finalParams,
        })
        .pipe(
          map((response) => {
            return this.updateTenantsLogoUrl(response);
          }),
          catchError((error: unknown) => this.handleError$(error)),
        );
    } else {
      const parentTenantId = String(pageRequest.tenantId);
      return this.http
        .get<TenantListPage<TenantRow>>(`${this.tenantBaseUrl}children`, {
          headers: this.setTenantIdHeader(new HttpHeaders(), parentTenantId),
          params: finalParams,
        })
        .pipe(
          map((response) => {
            return this.updateTenantsLogoUrl(response);
          }),
          catchError((error: unknown) => this.handleError$(error)),
        );
    }
  }

  getParentTenantList$(
    pageRequest: PageRequest,
    sortFilterContext: SortFilterContext,
    query?: string,
    dropTenantId?: string,
  ): Observable<TenantListPage<TenantRow>> {
    const params = new HttpParams()
      .set('perPage', pageRequest.perPage)
      .set('pageNum', pageRequest.pageNum)
      .set('sortFilterOpts', JSON.stringify(sortFilterContext ?? {}));

    const finalParams = query ? params.set('query', query) : params;
    const finalParamsWithDrop = dropTenantId ? finalParams.set('dropTenantId', dropTenantId) : finalParams;
    return this.http
      .get<TenantListPage<TenantRow>>(`${this.userBaseUrl}parents`, {
        headers: this.setTenantIdHeader(new HttpHeaders(), pageRequest.tenantId ? String(pageRequest.tenantId) : ''),
        params: finalParamsWithDrop,
      })
      .pipe(
        map((response) => {
          return this.updateTenantsLogoUrl(response);
        }),
        catchError((error: unknown) => this.handleError$(error)),
      );
  }

  private updateTenantsLogoUrl(response: TenantListPage<TenantRow>): TenantListPage<TenantRow> {
    const tenantRows = response.tenants.records;
    const updatedTenantRows: TenantRow[] = tenantRows.map((tenant) => ({
      ...tenant,
      logoUrl: tenant.logoUrl
        ? replaceTenantLogoUrlHost(tenant.logoUrl, this.configService.config.csPortalUrl)
        : tenant.logoUrl,
    }));
    const res = {
      ...response,
      tenants: {
        ...response.tenants,
        records: updatedTenantRows,
      },
    };
    return res;
  }

  getUserMembership$(tenantId: string, userId: string): Observable<UserMembership> {
    const url = `${this.userBaseUrl}${userId}/membership`;
    return this.http
      .get<UserMembership>(url, { headers: this.setTenantIdHeader(new HttpHeaders(), tenantId) })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getOpaPolicy$(): Observable<ArrayBuffer> {
    const releaseTag = getEnvOrDefault(ENV_VARS.RELEASE_TAG, 'local');
    const policyUrl = `${this.baseUrl}/assets/opa/policy-${releaseTag}.wasm`;
    return this.http
      .get(policyUrl, { responseType: 'arraybuffer' })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  configureCommandRequest(commandRequest: CommandRequest): CommandRequest {
    commandRequest.withHttpClient(this.http).withSignalR(this.signalRFacade);
    return commandRequest;
  }

  private ignoreNotFoundError$(error: unknown): Observable<never> {
    const httpError = error as HttpErrorResponse;
    if (httpError.status === 404) {
      this.logger.warn('not found: ', error);
      return throwError(() => error);
    }
    return this.handleError$(error);
  }

  private handleError$(error: unknown): Observable<never> {
    this.logger.error('error: ', error);
    const httpError = error as HttpErrorResponse;
    // client errors
    if (httpError.error && httpError.status === 404) {
      const errorCode = httpError.error.errorCode;
      const error: SnackBarData = {
        message:
          getErrorMessage(errorCode) ||
          `We couldn't find what you are looking for. Please check the URL and try again.`,
        type: 'Error',
      };
      this.uiStateFacade.displayMessage(error);
      return throwError(() => error);
    }
    if (httpError.error && httpError.status === 400) {
      const errorCode = httpError.error.errorCode;
      if (errorCode === AppErrorCode.TenantArchivedReadOnly) {
        this.userDataFacade.noLastAccessedTenant();
        return throwError(() => httpError);
      }
      const error: SnackBarData = {
        message: getErrorMessage(errorCode) || 'The request was invalid.',
        type: 'Error',
      };

      this.uiStateFacade.displayMessage(error);
      return throwError(() => error);
    }
    if (httpError.status === 403) {
      let message = 'Insufficient Permissions. Redirecting to organizations.';
      if (this.router.url === '/eula' || this.router.url === '/dashboard') {
        message = 'Insufficient Permissions';
      }
      this.uiStateFacade.displayMessage({
        message: message,
        type: 'Error',
      });
      this.router.navigate([TENANT_MGMT_ROUTES.ORGANIZATIONS]);
      return throwError(() => error);
    }
    this.controlPageFacade.navigateToError(new ApiError(httpError));
    return throwError(() => error);
  }

  private handleTenantLogoUploadError$(error: unknown): Observable<never> {
    this.logger.error('tenant logo : ', 'Tenant logo size exceeds maximum allowed limit.');
    this.uiStateFacade.displayMessage({
      message: 'The tenant logo file is too large. File should be less than 125kB.',
      type: 'Error',
    });
    return throwError(() => error);
  }

  /**
   * Get user's roles across organizational hierarchy for a specific user
   */
  getUserRoleHierarchy$(tenantId: string, userId: string): Observable<UserRoleHierarchy> {
    const url = `${this.userBaseUrl}${userId}/roleHierarchy`;
    return this.http
      .get<UserRoleHierarchy>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), tenantId),
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  /**
   * Get provisioned services across the tenant hierarchy from the parent organization
   */
  getTenantResourceHierarchy$(tenantId: string): Observable<TenantResourceHierarchy> {
    const url = `${this.tenantBaseUrl}resourceHierarchy`;
    return this.http
      .get<TenantResourceHierarchy>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), tenantId),
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }
}

export const ApiEndPoints = {
  Version: '/api/version',
  AccessRequest: '/api/accessrequests',
  Catalog: '/api/configuration/catalogs',
  Eula: '/api/eula/',
  Entitlement: '/api/entitlement/',
  Feedback: '/api/feedbacks',
  Invitations: '/api/invitations/',
  Notification: '/api/messages/',
  NotificationCounts: '/api/messages/count',
  SuggestedTenantByCode: '/api/tenants/invitecode/',
  SuggestedTenants: '/api/tenants/suggested2/',
  Tenant: '/api/tenant/',
  Tenants: '/api/tenants/',
  Users: '/api/users/',
  Trial: '/api/trialcampaigns',
  TrialBundles: '/api/configuration/trialbundles',
  Roles: '/api/roles/',
  CspUrls: '/api/configuration/cspUrls',
};
